// ── Gamification.js — streaks, milestones, journey progress ──────────────
// All components check their feature flag before rendering.
// Use in Discover, Matches, Profile.
// Feature flags: gamification_streaks, gamification_milestones, gamification_journey

import { useState, useEffect, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import { useTr }  from '../utils/useTr';
import { getBEE_STAGES, computeJourneyProgress, computeHiveScore, hiveScoreTier, computeRole, getBeeRoles } from '../utils/beeJourney';

// ── Design tokens ─────────────────────────────────────────────────────────
const G = {
  gold:    'var(--gold)',
  goldPale:'var(--gold-pale)',
  goldBdr: 'var(--gold-border)',
  white:   'var(--white)',
  muted:   'var(--muted)',
  sans:    "'Outfit',sans-serif",
  serif:   "'Cormorant Garamond',serif",
};

// ── Milestone definitions ─────────────────────────────────────────────────
const MILESTONES = {
  first_like:       { icon:'♥',   title:'First like sent',       sub:'The journey begins.',                           color:'#C9A84C' },
  first_match:      { icon:'✦',   title:'First match!',          sub:'Someone likes you back.',                       color:'#E2C06E' },
  first_message:    { icon:'✉',   title:'First conversation',    sub:'You started something real.',                   color:'#7AB8C4' },
  match_90pct:      { icon:'⬟',   title:'90%+ compatibility',    sub:'Rare. Pay attention to this one.',              color:'#C9A84C' },
  match_95pct:      { icon:'◈',   title:'95%+ compatibility',    sub:'Exceptional match. Don\'t wait.',               color:'#FFD700' },
  streak_7:         { icon:'★',   title:'7-day streak',          sub:'You show up. That\'s rare.',                    color:'#E2785A' },
  streak_14:        { icon:'★',   title:'2-week streak',         sub:'Consistency is attractive.',                    color:'#E2785A' },
  streak_30:        { icon:'★',   title:'30-day streak',         sub:'You\'re one of us now.',                        color:'#FFD700' },
  profile_complete: { icon:'✓',   title:'Profile complete',      sub:'Friends can now rate you accurately.',          color:'#4EC994' },
  five_friends:     { icon:'✦',   title:'5 friends rated you',   sub:'Your profile is alive.',                        color:'#A084C9' },
  first_event:      { icon:'✦',   title:'First event attended',  sub:'You stepped into the hive.',                    color:'#C97A7A' },
  first_super_like: { icon:'★',   title:'First super like',      sub:'You meant it.',                                 color:'#C9A84C' },
  hive_score_50:    { icon:'⬡',   title:'Hive Pillar',           sub:'You\'ve built something here.',                 color:'#C9A84C' },
};

// ── Milestone celebration overlay ─────────────────────────────────────────
export function MilestoneCelebration({ milestone, onClose }) {
  const { features } = useApp();
  const [visible, setVisible] = useState(true);

  if (!features?.gamification_milestones || !milestone) return null;

  const def = MILESTONES[milestone.milestone] || { icon:'✦', title:milestone.milestone, sub:'Achievement unlocked', color:G.gold };

  const dismiss = () => { setVisible(false); setTimeout(onClose, 300); };

  return (
    <div onClick={dismiss} style={{
      position:'fixed', inset:0, zIndex:999,
      background:'rgba(0,0,0,0.85)',
      display:'flex', alignItems:'center', justifyContent:'center',
      opacity: visible ? 1 : 0, transition:'opacity 0.3s',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:'#161616', border:`1px solid ${def.color}40`,
        borderRadius:20, padding:'2rem 1.5rem',
        maxWidth:320, width:'100%', textAlign:'center',
        transform: visible ? 'scale(1)' : 'scale(0.9)',
        transition:'transform 0.3s',
      }}>
        {/* Glow ring */}
        <div style={{ width:80, height:80, borderRadius:'50%', background:`${def.color}15`, border:`2px solid ${def.color}40`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1rem', fontSize:'2rem' }}>
          {def.icon}
        </div>

        <p style={{ fontFamily:G.serif, fontSize:'1.6rem', fontWeight:400, color:'#F2E8D0', margin:'0 0 0.4rem' }}>
          {def.title}
        </p>
        <p style={{ fontSize:'0.85rem', color:'rgba(255,255,255,0.55)', lineHeight:1.6, margin:'0 0 1.5rem' }}>
          {def.sub}
        </p>

        {milestone.data?.compatScore && (
          <div style={{ background:`${def.color}10`, border:`1px solid ${def.color}30`, borderRadius:99, padding:'0.4rem 1rem', display:'inline-block', marginBottom:'1rem' }}>
            <span style={{ fontSize:'0.8rem', color:def.color, fontWeight:600 }}>
              {milestone.data.compatScore}% compatibility
            </span>
          </div>
        )}

        <button onClick={dismiss} style={{ width:'100%', padding:'0.8rem', background:`${def.color}20`, border:`1px solid ${def.color}40`, borderRadius:10, color:def.color, fontFamily:G.sans, fontWeight:600, fontSize:'0.9rem', cursor:'pointer' }}>
          ✦ Continue
        </button>
      </div>
    </div>
  );
}

// ── Streak pill — shown in Discover header ────────────────────────────────
export function StreakPill({ userId }) {
  const { features } = useApp();
  const [streak, setStreak] = useState(null);
  const [boosted, setBoosted] = useState(false);

  useEffect(() => {
    if (!features?.gamification_streaks || !userId) return;
    import('../utils/engagement').then(({ getStreak }) => {
      getStreak(userId).then(s => {
        setStreak(s.days);
        setBoosted(s.boosted);
      });
    });
  }, [userId, features?.gamification_streaks]);

  if (!features?.gamification_streaks || !streak || streak < 2) return null;

  const color = streak >= 30 ? '#FFD700' : streak >= 14 ? '#E2785A' : streak >= 7 ? '#E2785A' : G.gold;

  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 10px', borderRadius:99, background:`${color}15`, border:`1px solid ${color}35` }}>
      <span style={{ fontSize:11 }}>★</span>
      <span style={{ fontSize:'0.68rem', fontWeight:700, color, fontFamily:G.sans }}>
        {streak}
      </span>
      {boosted && <span style={{ fontSize:9, color, fontFamily:G.sans }}>↑</span>}
    </div>
  );
}

// ── Journey progress bar — shown in Profile ───────────────────────────────
export function JourneyProgress({ user, matches, lang }) {
  const { features } = useApp();
  const tr = useTr();

  if (!features?.gamification_journey) return null;

  const progress = computeJourneyProgress(user, {}, matches || [], lang);
  const completed = progress.filter(s => s.completed).length;
  const total     = progress.length;
  const current   = progress.filter(s => s.completed).pop();
  const next      = progress.find(s => !s.completed);

  // Stage names
  const stageNames = ['New Bee', 'Vouched', 'Complete', 'Connected', 'Builder', 'Loved'];

  return (
    <div style={{ background:'var(--dark)', border:'1px solid var(--border)', borderRadius:12, padding:'1rem', marginBottom:'1rem' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'0.6rem' }}>
        <span style={{ fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.1em', color:G.muted, textTransform:'uppercase' }}>
          {tr('journey.progress') || 'Your journey'}
        </span>
        <span style={{ fontSize:'0.75rem', fontWeight:600, color:G.gold }}>
          {stageNames[completed - 1] || 'New Bee'}
        </span>
      </div>

      {/* Progress bar */}
      <div style={{ height:4, background:'var(--border2)', borderRadius:99, marginBottom:'0.7rem', overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${(completed/total)*100}%`, background:`linear-gradient(90deg, ${G.gold}, #E2C06E)`, borderRadius:99, transition:'width 0.6s ease' }}/>
      </div>

      {/* Stage dots */}
      <div style={{ display:'flex', justifyContent:'space-between' }}>
        {progress.map((stage, i) => (
          <div key={stage.id} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
            <div style={{ width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10,
              background: stage.completed ? G.gold : 'var(--border2)',
              border: `1.5px solid ${stage.completed ? G.gold : 'var(--border)'}`,
              color: stage.completed ? '#0A0A0A' : G.muted,
            }}>
              {stage.completed ? '✓' : i + 1}
            </div>
            <span style={{ fontSize:'0.55rem', color: stage.completed ? G.gold : G.muted, textAlign:'center', maxWidth:36, lineHeight:1.2 }}>
              {stageNames[i]}
            </span>
          </div>
        ))}
      </div>

      {/* Next goal */}
      {next && (
        <div style={{ marginTop:'0.7rem', padding:'0.5rem 0.7rem', background:'rgba(201,168,76,0.06)', border:'1px solid rgba(201,168,76,0.15)', borderRadius:8 }}>
          <p style={{ fontSize:'0.7rem', color:G.muted, margin:0 }}>
            <span style={{ color:G.gold }}>Next:</span> {next.desc}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Hive score badge — shown in BeeHive ───────────────────────────────────
export function HiveScoreBadge({ user, stats }) {
  const { features } = useApp();
  if (!features?.gamification_hive_score) return null;

  const score = computeHiveScore(user, stats || {});
  const tier  = hiveScoreTier(score);

  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px', borderRadius:99, background:`${tier.color}15`, border:`1px solid ${tier.color}30` }}>
      <span style={{ fontSize:11 }}>⬡</span>
      <span style={{ fontSize:'0.7rem', fontWeight:600, color:tier.color, fontFamily:G.sans }}>{score}</span>
      <span style={{ fontSize:'0.65rem', color:G.muted, fontFamily:G.sans }}>{tier.label}</span>
    </div>
  );
}

// ── Role badge ────────────────────────────────────────────────────────────
export function RoleBadge({ user, stats, conversations, matches, lang }) {
  const { features } = useApp();
  if (!features?.gamification_journey) return null;

  const role = computeRole(user, stats, conversations, matches);
  if (!role) return null;

  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:99, background:`${role.color}15`, border:`1px solid ${role.color}30` }}>
      <span style={{ fontSize:'0.68rem', fontWeight:600, color:role.color, fontFamily:G.sans }}>
        {role.name}
      </span>
    </div>
  );
}

// ── Milestone checker hook — run after key actions ────────────────────────
export function useMilestoneChecker() {
  const { currentUser, features } = useApp();
  const [pending, setPending] = useState(null);

  const checkMilestone = async (milestone, data = {}) => {
    if (!features?.gamification_milestones || !currentUser?.id) return;
    try {
      const { supabase } = await import('../utils/supabase');
      const { data: result } = await supabase.rpc('award_milestone', {
        p_user_id:   currentUser.id,
        p_milestone: milestone,
        p_data:      data,
      });
      if (result) {
        // New milestone — show celebration
        setPending({ milestone, data });
      }
    } catch {}
  };

  const checkStreaks = async () => {
    if (!features?.gamification_streaks || !currentUser?.id) return;
    try {
      const { supabase } = await import('../utils/supabase');
      await supabase.rpc('check_streak_milestones', { p_user_id: currentUser.id });
    } catch {}
  };

  return { checkMilestone, checkStreaks, pending, clearPending: () => setPending(null) };
}

// ── Rating streak badge — Feature E (feature_rating_streak) ──────────────────
// Counts consecutive weeks (ending current week) with ≥1 rating received.
// Renders nothing when feature is off, when streak is 0, or when no user.
export function RatingStreakBadge({ inline = false, lang = 'en' }) {
  const { features, currentUser } = useApp();
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    if (!features?.feature_rating_streak || !currentUser?.id) return;
    let alive = true;
    import('../utils/featurePackA').then(({ fetchRatingStreak }) => {
      fetchRatingStreak(currentUser.id).then(n => { if (alive) setStreak(n); });
    });
    return () => { alive = false; };
  }, [features?.feature_rating_streak, currentUser?.id]);

  if (!features?.feature_rating_streak || streak <= 0) return null;
  const fr = lang === 'fr';
  const label = streak === 1
    ? (fr ? '1 semaine' : '1 week')
    : `${streak} ${fr ? 'semaines' : 'weeks'}`;
  const sub = fr ? 'd\'avis reçus consécutifs' : 'in a row of friend ratings';

  if (inline) {
    return (
      <span title={sub} style={{
        display:'inline-flex', alignItems:'center', gap:6,
        background:'rgba(226,120,90,0.12)', border:'1px solid rgba(226,120,90,0.4)',
        borderRadius:99, padding:'3px 10px',
        fontFamily:G.sans, fontSize:11, fontWeight:600, color:'#E2785A',
      }}>
        🔥 {label}
      </span>
    );
  }

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:10,
      background:'rgba(226,120,90,0.06)', border:'1px solid rgba(226,120,90,0.25)',
      borderRadius:10, padding:'10px 14px',
    }}>
      <div style={{ fontSize:24 }}>🔥</div>
      <div>
        <p style={{ margin:0, fontFamily:G.serif, fontSize:18, color:G.white }}>{label}</p>
        <p style={{ margin:0, fontSize:11, color:G.muted }}>{sub}</p>
      </div>
    </div>
  );
}
